
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
    CategoryModel,
    PlanItemModel,
    PlanItemProductGroupProcessStepModel,
    ProductGroupModel,
    ResponseModel,
    ResponseTypeEnum,
    SummarizationTypeEnum,
} from '@/libs/Api';
import { QuestionGroupResponse } from '@/libs/work-tasks/model/QuestionGroupResponse';
import { SystemAuditValueEnum } from '@/libs/core/models/system-audit-value-enum';
import { Guid } from '@/libs/common/functions/guid';

@Component({})
export default class WorkTaskSummary extends Vue {
    @Prop({ type: Array, default: () => [] }) items!: Array<QuestionGroupResponse>;
    @Prop({ type: Boolean, default: () => false }) isProductAudit!: boolean;
    @Prop({ type: Boolean, default: () => false }) isVda63!: boolean;
    @Prop() category!: CategoryModel;
    @Prop() item!: PlanItemModel;

    protected SummarizationTypeEnum: typeof SummarizationTypeEnum = SummarizationTypeEnum;

    get productAuditSummary(): {
        index: string;
        category: string;
        question: string;
        defectA: number;
        defectB: number;
        defectC: number;
    }[] {
        const summaryItems: {
            index: string;
            category: string;
            question: string;
            defectA: number;
            defectB: number;
            defectC: number;
        }[] = [];
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers.forEach((q) => {
                const response = q.responses[0];
                summaryItems.push({
                    index: q.question.code ?? '',
                    category: item.name!,
                    question: q.question!.name ?? '',
                    defectA:
                        response.productAuditValue == 1 ? response.responseParts!.filter((t) => t.isOk == false).length : 0,
                    defectB:
                        response.productAuditValue == 2 ? response.responseParts!.filter((t) => t.isOk == false).length : 0,
                    defectC:
                        response.productAuditValue == 3 ? response.responseParts!.filter((t) => t.isOk == false).length : 0,
                });
            });
        });
        return summaryItems;
    }

    get productAuditSummaryTotals(): { defectA: number; defectB: number; defectC: number } {
        const summaryItems: { defectA: number; defectB: number; defectC: number } = {
            defectA: 0,
            defectB: 0,
            defectC: 0,
        };
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers.forEach((q) => {
                const response = q.responses[0];
                summaryItems.defectA +=
                    response.productAuditValue == 1 ? response.responseParts!.filter((t) => t.isOk == false).length : 0;
                summaryItems.defectB +=
                    response.productAuditValue == 2 ? response.responseParts!.filter((t) => t.isOk == false).length : 0;
                summaryItems.defectC +=
                    response.productAuditValue == 3 ? response.responseParts!.filter((t) => t.isOk == false).length : 0;
            });
        });
        return summaryItems;
    }

    get unansweredQuestionsCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (this.category?.responseType == ResponseTypeEnum.ProductAudit) {
                        if (response.productAuditValue == null) {
                            result++;
                        }
                    } else if (
                        !response.scoreValue &&
                        response.scoreValue != 0 &&
                        !response.okValue &&
                        !response.systemAuditValue
                    ) {
                        result++;
                    }
                });
        });
        return result;
    }

    get missingDescriptionsCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.description == '') result++;
                });
        });
        return result;
    }

    get missingImmediateMeasuresCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.immediateMeasures == '') result++;
                });
        });
        return result;
    }

    get missingEscalationDescriptionCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    response.findings?.forEach((finding: ResponseModel) => {
                        if (response.isOk == false && (finding.description == '' || finding.description == null)) result++;
                    });
                });
        });
        return result;
    }

    get okResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.isOk && response.okValue == 1) result++;
                });
        });
        return result;
    }

    get systemAuditOkResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (
                        response.isOk &&
                        (response.systemAuditValue == SystemAuditValueEnum.Compliant ||
                            response.systemAuditValue == SystemAuditValueEnum.OportunityForImprovement ||
                            response.systemAuditValue == SystemAuditValueEnum.PositiveAspect)
                    )
                        result++;
                });
        });
        return result;
    }

    get nokResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.isOk == false) result++;
                });
        });
        return result;
    }

    get okWithWarningResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.isOk && response.okValue == 2) result++;
                });
        });
        return result;
    }

    get scoreZeroResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 0) result++;
                });
        });
        return result;
    }

    get scoreOneResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 1) result++;
                });
        });
        return result;
    }

    get scoreTwoResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 2) result++;
                });
        });
        return result;
    }

    get scoreThreeResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 3) result++;
                });
        });
        return result;
    }

    get scoreFourResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 4) result++;
                });
        });
        return result;
    }

    get scoreFiveResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 5) result++;
                });
        });
        return result;
    }

    get scoreSixResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 6) result++;
                });
        });
        return result;
    }

    get scoreEightResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 8) result++;
                });
        });
        return result;
    }

    get scoreTenResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.scoreValue == 10) result++;
                });
        });
        return result;
    }

    get productAuditOkCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.productAuditValue == 0) {
                        result += response!.responseParts!.filter((x) => x.isOk == true).length ?? 0;
                    }
                });
        });
        return result;
    }

    get defectA(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.productAuditValue == 1) {
                        result += response!.responseParts!.filter((x) => x.isOk == false).length ?? 0;
                    }
                });
        });
        return result;
    }

    get defectB(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.productAuditValue == 2) {
                        result += response!.responseParts!.filter((x) => x.isOk == false).length ?? 0;
                    }
                });
        });
        return result;
    }

    get defectC(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (response.productAuditValue == 3) {
                        result += response!.responseParts!.filter((x) => x.isOk == false).length ?? 0;
                    }
                });
        });
        return result;
    }

    get notRatedResponseCount(): number {
        let result = 0;
        this.items.forEach((item: QuestionGroupResponse) => {
            item.questionContainers
                .selectMany((n) => n.responses!)
                .toArray()
                .forEach((response: ResponseModel) => {
                    if (
                        this.category!.responseType == 'Ok' ||
                        this.category!.responseType == 'OkNok' ||
                        this.category!.responseType == 'OkNokWarning' ||
                        this.category!.responseType == 'NokWarning'
                    ) {
                        if (response.okValue == -1) {
                            result++;
                        }
                    } else if (this.category!.responseType == 'SystemAudit') {
                        if (response.systemAuditValue == -1) {
                            result++;
                        }
                    } else if (this.category!.responseType == 'ProductAudit') {
                        if (response.productAuditValue == -1) {
                            result++;
                        }
                    } else {
                        if (response.scoreValue == -1) {
                            result++;
                        }
                    }
                });
        });
        return result;
    }

    get summarizationProductGroups(): {
        productGroupId: string | null;
        productGroup: ProductGroupModel | null;
        productGroupProcessSteps: PlanItemProductGroupProcessStepModel[];
    }[] {
        const items =
            this.item?.summarizations
                ?.groupBy((x) => x.productGroupId ?? Guid.EmptyGuid())
                .orderBy((x) => x.first().rowIndex)
                .select((x) => {
                    return {
                        productGroupId: x.first().productGroupId ?? null,
                        productGroup: x.first().productGroup ?? null,
                        productGroupProcessSteps:
                            this.item?.productGroupProcessSteps
                                ?.where((t) => t.productGroupId == (x.first().productGroupId ?? Guid.EmptyGuid()))
                                .toArray() ?? [],
                    };
                })
                .toArray() ?? [];
        return items;
    }

    get totalScorePercent(): string {
        if (this.item) {
            if (this.item.responseLock == true) {
                if (
                    this.item!.audit!.category!.responseType == 'Ok' ||
                    this.item!.audit!.category!.responseType == 'OkNok' ||
                    this.item!.audit!.category!.responseType == 'OkNokWarning' ||
                    this.item!.audit!.category!.responseType == 'NokWarning'
                ) {
                    const responseCount = this.item.responseCount!;
                    const okCount = this.item.okCount! + this.item.okWarningCount!;
                    if (responseCount == 0) return '100%';
                    return ((okCount / responseCount) * 100).toFixed(2) + '%';
                } else if (this.item!.audit!.category!.responseType == 'SystemAudit') {
                    const responseCount = this.item.responseCount!;
                    const okCount = this.item.okCount! + this.item.okWarningCount!;
                    if (responseCount == 0) return '100%';
                    return ((okCount / responseCount) * 100).toFixed(2) + '%';
                } else if (this.item!.audit!.category!.responseType == 'ProductAudit') {
                    const responseCount = this.item.responseCount!;
                    const okCount = this.item.okCount! + this.item.okWarningCount!;
                    if (responseCount == 0) return '100%';
                    return ((okCount / responseCount) * 100).toFixed(2) + '%';
                } else {
                    const scoreValue = this.item.scoreValue!;
                    const scoreMaxValue = this.item.scoreMaxValue!;
                    if (scoreValue == null || scoreMaxValue == null) return '--';
                    if (scoreMaxValue == 0) return '100%';
                    return ((scoreValue / scoreMaxValue) * 100).toFixed(2) + '%';
                }
            } else {
                if (
                    this.item!.audit!.category!.responseType == 'Ok' ||
                    this.item!.audit!.category!.responseType == 'OkNok' ||
                    this.item!.audit!.category!.responseType == 'OkNokWarning' ||
                    this.item!.audit!.category!.responseType == 'NokWarning'
                ) {
                    const responseCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.okValue != -1)
                            .count()
                    );
                    const okCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.okValue != -1)
                            .count((n) => n.okValue == 1 || n.okValue == 2)
                    );
                    if (responseCount == 0) return '100%';
                    return ((okCount / responseCount) * 100).toFixed(2) + '%';
                } else if (this.item!.audit!.category!.responseType == 'SystemAudit') {
                    const responseCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.systemAuditValue != -1)
                            .count()
                    );
                    const okCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.systemAuditValue != -1)
                            .count(
                                (n) =>
                                    n.systemAuditValue == SystemAuditValueEnum.Compliant ||
                                    n.systemAuditValue == SystemAuditValueEnum.OportunityForImprovement ||
                                    n.systemAuditValue == SystemAuditValueEnum.PositiveAspect
                            )
                    );
                    if (responseCount == 0) return '100%';
                    return ((okCount / responseCount) * 100).toFixed(2) + '%';
                } else if (this.item!.audit!.category!.responseType == 'ProductAudit') {
                    const responseCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.productAuditValue != -1)
                            .count()
                    );
                    const okCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.productAuditValue != -1)
                            .count((n) => n.productAuditValue == 0)
                    );
                    if (responseCount == 0) return '100%';
                    return ((okCount / responseCount) * 100).toFixed(2) + '%';
                } else {
                    const responseCount = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.okValue != -1)
                            .count()
                    );
                    let maxScorePerQuestions = 0;
                    switch (this.category!.responseType) {
                        case ResponseTypeEnum.ScoreOneToFive:
                            maxScorePerQuestions = 5;
                            break;
                        case ResponseTypeEnum.ScoreOneToThree:
                            maxScorePerQuestions = 3;
                            break;
                        case ResponseTypeEnum.ScoreZeroToFive:
                            maxScorePerQuestions = 5;
                            break;
                        case ResponseTypeEnum.ScoreZeroToFour:
                            maxScorePerQuestions = 4;
                            break;
                        case ResponseTypeEnum.ScoreZeroToOne:
                            maxScorePerQuestions = 1;
                            break;
                        case ResponseTypeEnum.ScoreZeroToTwo:
                            maxScorePerQuestions = 2;
                            break;
                        case ResponseTypeEnum.VDA63:
                            maxScorePerQuestions = 10;
                            break;
                    }
                    const scoreValue = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.scoreValue != -1)
                            .sum((p) => p.scoreValue ?? 0)
                    );
                    const scoreMaxValue = this.items.sum((x) =>
                        x.questionContainers
                            .selectMany((n) => n.responses!)
                            .where((n) => n.scoreValue != -1)
                            .sum((p) => maxScorePerQuestions)
                    );
                    if (scoreValue == null || scoreMaxValue == null) return '--';
                    if (scoreMaxValue == 0) return '100%';
                    return ((scoreValue / scoreMaxValue) * 100).toFixed(2) + '%';
                }
            }
        }
        return '--';
    }

    isUsingNotRatedResponseOption(): boolean {
        return this.items.any((x) =>
            x.questionContainers.selectMany((n) => n.responses!).any((r) => this.canShowNotRatedQuestionOption(r))
        );
    }

    canShowNotRatedQuestionOption(response: ResponseModel): boolean {
        if (this.category!.useNotRatedOption) {
            return true;
        } else if (
            (this.category!.responseType == 'Ok' ||
                this.category!.responseType == 'OkNok' ||
                this.category!.responseType == 'OkNokWarning' ||
                this.category!.responseType == 'NokWarning') &&
            response.okValue == -1
        ) {
            return true;
        } else if (this.category!.responseType == 'SystemAudit' && response.systemAuditValue == -1) {
            return true;
        } else if (this.category!.responseType == 'ProductAudit' && response.productAuditValue == -1) {
            return true;
        } else if (
            (this.category!.responseType == 'ScoreZeroToOne' ||
                this.category!.responseType == 'ScoreZeroToTwo' ||
                this.category!.responseType == 'ScoreOneToThree' ||
                this.category!.responseType == 'ScoreZeroToFour' ||
                this.category!.responseType == 'ScoreZeroToFive' ||
                this.category!.responseType == 'ScoreOneToFive') &&
            response.scoreValue == -1
        ) {
            return true;
        }
        return false;
    }
}
